/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Divider, Input, Modal, Row, Select, Space, Tag, Typography, Image, Upload } from 'antd';
import styles from 'src/styles/layout.module.css';
import { useDispatch } from 'react-redux';
import {
  createCompanyPaymentProvider,
  deleteCompanyPaymentProvider,
  fetchCompanyPaymentProviders,
  fetchPaymentMethods,
  updateCompanyPaymentMethods,
} from 'src/features/paymentProvider/paymentProvider.state';
import { useParams } from 'react-router-dom';
import {
  usePaymentMethods,
  usePaymentMethodsByCompanyId,
  usePaymentProvidersByCompanyId,
} from 'src/features/paymentProvider/paymentProvider.hooks';
import { PAYMENT_PROVIDERS } from 'src/features/paymentProvider/paymentProviders.constants';
import { DraftCompanyPaymentProvider } from 'src/features/paymentProvider/paymentProvider.types';
import tileStyles from 'src/styles/tile.module.css';

import { ApiTwoTone, CheckCircleTwoTone, DeleteOutlined, DollarTwoTone } from '@ant-design/icons';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import EditableField from 'src/components/fields/EditableField';
import moment from 'moment';
import cx from 'classnames';
import InputNumberEditableField from 'src/components/fields/InputNumberEditableField';
import { useCompanyById } from 'src/features/companies/companies.hooks';
import { Company, DraftCompany } from 'src/features/companies/companies.types';
import { AppDispatch } from 'src/index';
import { useLocationsValues } from 'src/features/locations/locations.hooks';
import { format, set } from 'date-fns';
import {
  activateCompany,
  deleteCompany,
  fetchCompany,
  generateHeadsetActivationCode,
  updateCompany,
} from 'src/features/companies/companies.state';
import { fetchLocations } from 'src/features/locations/locations.state';
import { fetchTimezones } from 'src/features/timezones/timezones.state';
import InputEditableField from 'src/components/fields/InputEditableField';
import { UploadFile } from 'antd/es/upload/interface';
import { useCompanyImage } from 'src/features/companies/companies.hooks';
const { Option } = Select;

let timezones: string[] = [];

const checkInHours = Array.from(new Array(24)).map((h, i) => {
  const d = set(new Date(), { hours: i });
  return format(d, 'HH:00');
});

const CompanySettingsSection: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch: AppDispatch = useDispatch();
  const company = useCompanyById(companyId) as Company;
  const locations = useLocationsValues();

  useEffect(() => {
    dispatch(fetchCompany({ companyId }));

    (async () => {
      const response = await dispatch(fetchTimezones({}));

      if (fetchTimezones.fulfilled.match(response)) {
        timezones = response.payload;
      }
    })();
  }, [companyId, dispatch]);

  const parseDate = useCallback((date: string) => {
    if (!date) {
      return '';
    }
    return format(new Date(date), 'd MMMM y hh:mm');
  }, []);

  const handleGetCodeButtonClick = useCallback(async () => {
    const response = await dispatch(generateHeadsetActivationCode({ companyId }));

    if (generateHeadsetActivationCode.fulfilled.match(response)) {
      Modal.info({
        title: 'Код для активации',
        content: response.payload.code,
      });
    }
  }, [companyId, dispatch]);

  const handleCompanyActivation = useCallback(() => {
    dispatch(activateCompany({ companyId }));
  }, [companyId, dispatch]);

  const handleDeleteButtonClick = useCallback(() => {
    dispatch(deleteCompany({ companyId }));
  }, [companyId, dispatch]);

  const handleCompanyUpdate = useCallback(
    (data: Partial<DraftCompany>) => {
      dispatch(updateCompany({ companyId, data }));
    },
    [companyId, dispatch]
  );

  useEffect(() => {
    dispatch(fetchLocations({}));
  }, [companyId, dispatch]);

  return (
    <div className={styles.sidebar} style={{ marginBottom: '8em', padding: 0 }}>
      <Space direction='vertical'>
        {!company.isActive && (
          <Button type='primary' onClick={handleCompanyActivation}>
            Активировать
          </Button>
        )}
        {company.isActive && (
          <Tag color='green'>
            <Space>
              <CheckCircleTwoTone />
              Компания активирована
            </Space>
          </Tag>
        )}
        {company.disabled ? (
          <Button type='primary' onClick={() => handleCompanyUpdate({ disabled: !company.disabled })}>
            Разблокировать
          </Button>
        ) : (
          <ConfirmButton type='primary' onClick={() => handleCompanyUpdate({ disabled: !company.disabled })} danger>
            Заблокировать
          </ConfirmButton>
        )}
        <Button type='dashed' onClick={handleGetCodeButtonClick}>
          Получить код
        </Button>
      </Space>
      <div className={styles.propertiesGrid}>
        <Typography.Text className={styles.title}>Название</Typography.Text>
        <InputEditableField value={company.name} onBlur={e => handleCompanyUpdate({ name: e })} />
        <Typography.Text className={styles.title}>Номер телефона</Typography.Text>
        <InputEditableField value={company.phoneNumber || ''} onBlur={e => handleCompanyUpdate({ phoneNumber: e })} />
        <Typography.Text className={styles.title}>Тестовая</Typography.Text>
        <EditableField value={company.isTest ? 'Да' : 'Нет'}>
          <Select
            placeholder='Please select a location'
            value={company.isTest ? 'Да' : 'Нет'}
            onSelect={e => handleCompanyUpdate({ isTest: e })}
            style={{ width: '100%' }}
            defaultOpen
          >
            {[
              { label: 'Да', value: 1 },
              { label: 'Нет', value: 0 },
            ].map(l => (
              <Option key={l.label} value={l.value}>
                {l.label}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Локация</Typography.Text>
        <EditableField value={company.location.name}>
          <Select
            placeholder='Please select a location'
            value={company.location.code}
            onSelect={(e: string) => handleCompanyUpdate({ location: e })}
            style={{ width: '100%' }}
            defaultOpen
          >
            {locations.map(l => (
              <Option key={l.code} value={l.code}>
                {l.name}
              </Option>
            ))}
          </Select>
        </EditableField>

        <Typography.Text className={styles.title}>Дата активации</Typography.Text>
        {company.activatedAt ? (
          <EditableField value={parseDate(company.activatedAt)} disableClickAway>
            <DatePicker
              showTime
              defaultValue={company.activatedAt ? moment(company.activatedAt, 'YYYY-MM-DD') : undefined}
              onChange={(date, dateString) => handleCompanyUpdate({ activatedAt: dateString })}
            />
          </EditableField>
        ) : (
          <Typography.Text>Не активирована</Typography.Text>
        )}
        <Typography.Text className={styles.title}>Пользовательская сессия</Typography.Text>
        <EditableField value={company.needCustomerSession ? 'Да' : 'Нет'}>
          <Select
            placeholder='Please select a location'
            value={company.needCustomerSession ? 'Да' : 'Нет'}
            onSelect={e => handleCompanyUpdate({ needCustomerSession: e })}
            style={{ width: '100%' }}
            defaultOpen
          >
            {[
              { label: 'Да', value: 1 },
              { label: 'Нет', value: 0 },
            ].map(l => (
              <Option key={l.label} value={l.value}>
                {l.label}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Время заезда</Typography.Text>
        <EditableField
          value={company.checkinTime}
          className={cx({
            [styles.muted]: !company.needCustomerSession,
          })}
        >
          <Select
            value={company.checkinTime}
            onSelect={e => handleCompanyUpdate({ checkinTime: e })}
            style={{ width: '100%' }}
            disabled={!company.needCustomerSession}
            defaultOpen
          >
            {checkInHours.map(l => (
              <Option key={l} value={l}>
                {l}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Время выезда</Typography.Text>
        <EditableField
          value={company.checkoutTime}
          className={cx({
            [styles.muted]: !company.needCustomerSession,
          })}
        >
          <Select
            value={company.checkoutTime}
            onSelect={e => handleCompanyUpdate({ checkoutTime: e })}
            style={{ width: '100%' }}
            disabled={!company.needCustomerSession}
            defaultOpen
          >
            {checkInHours.map(l => (
              <Option key={l} value={l}>
                {l}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Часовой пояс</Typography.Text>
        <EditableField
          value={company.timezone}
          className={cx({
            [styles.muted]: !company.needCustomerSession,
          })}
        >
          <Select
            value={company.timezone}
            onSelect={e => handleCompanyUpdate({ timezone: e })}
            style={{ width: '100%' }}
            disabled={!company.needCustomerSession}
            defaultOpen
            showSearch
            autoClearSearchValue
            autoFocus
          >
            {timezones.map(l => (
              <Option key={l} value={l}>
                {l}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Качество видео</Typography.Text>
        <EditableField value={company.settings.defaultVideoQuality || '-'}>
          <Select
            value={company.settings.defaultVideoQuality}
            onSelect={e => handleCompanyUpdate({ defaultVideoQuality: e })}
            style={{ width: '100%' }}
            defaultOpen
            showSearch
            autoClearSearchValue
            autoFocus
          >
            {['-', 'hd', 'full_hd', '2k', '4k'].map(l => (
              <Option key={l} value={l}>
                {l}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Процент заселения</Typography.Text>
        <InputNumberEditableField
          value={company.defaultCheckinPercentage}
          onBlur={e => handleCompanyUpdate({ defaultCheckinPercentage: e })}
          min={0}
          max={100}
        />
        <Typography.Text className={styles.title}>Показывать 18+</Typography.Text>
        <EditableField value={company.settings.adult ? 'Да' : 'Нет'}>
          <Select
            placeholder='Please select a location'
            value={company.settings.adult ? 'Да' : 'Нет'}
            onSelect={e => handleCompanyUpdate({ adult: e })}
            style={{ width: '100%' }}
            defaultOpen
          >
            {[
              { label: 'Да', value: 1 },
              { label: 'Нет', value: 0 },
            ].map(l => (
              <Option key={l.label} value={l.value}>
                {l.label}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Показывать магазин</Typography.Text>
        <EditableField value={company.settings.vrShop ? 'Да' : 'Нет'}>
          <Select
            placeholder='Please select a location'
            value={company.settings.vrShop ? 'Да' : 'Нет'}
            onSelect={e => handleCompanyUpdate({ vrShop: e })}
            style={{ width: '100%' }}
            defaultOpen
          >
            {[
              { label: 'Да', value: 1 },
              { label: 'Нет', value: 0 },
            ].map(l => (
              <Option key={l.label} value={l.value}>
                {l.label}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Активировать триал</Typography.Text>
        <EditableField value={company.settings.trialEnabled ? 'Да' : 'Нет'}>
          <Select
            placeholder='Please select a location'
            value={company.settings.adult ? 1 : 0}
            onSelect={e => handleCompanyUpdate({ trialEnabled: Boolean(e) })}
            style={{ width: '100%' }}
            defaultOpen
          >
            {[
              { label: 'Да', value: 1 },
              { label: 'Нет', value: 0 },
            ].map(l => (
              <Option key={l.label} value={l.value}>
                {l.label}
              </Option>
            ))}
          </Select>
        </EditableField>
        <Typography.Text className={styles.title}>Срок триала в сек.</Typography.Text>
        {company.settings.trialEnabled ? (
          <InputNumberEditableField
            value={company.settings.trialTime || 0}
            onBlur={e => handleCompanyUpdate({ trialTime: e })}
            min={0}
          />
        ) : (
          <Typography.Text disabled={!company.settings.trialEnabled}>{company.settings.trialTime || 0}</Typography.Text>
        )}
      </div>
      <Divider />
      <Space direction='vertical' size='small'>
        <Typography.Text className={styles.footerDateStamp}>Создано: {parseDate(company.createdAt)}</Typography.Text>
        <Typography.Text className={styles.footerDateStamp}>Обновлено: {parseDate(company.updatedAt)}</Typography.Text>
        <ConfirmButton type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
          <DeleteOutlined /> Удалить компанию
        </ConfirmButton>
      </Space>
    </div>
  );
};

const CompanySettingsTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const dispatch = useDispatch();
  const companyPaymentProviders = usePaymentProvidersByCompanyId({ companyId });
  const paymentMethods = usePaymentMethods();
  const companyPaymentMethods = usePaymentMethodsByCompanyId({ companyId });
  const company = useCompanyById(companyId) as Company;

  const [newPaymentProvider, setNewPaymentProvider] = useState<DraftCompanyPaymentProvider>({
    credentials: {},
    provider: '',
  });

  const mergedPaymentMethods = useMemo(() => {
    return paymentMethods.map(m => ({
      method: m,
      enabled: companyPaymentMethods.includes(m),
    }));
  }, [companyPaymentMethods, paymentMethods]);

  const handleUpdateCompanyPaymentMethods = useCallback(
    ({ method, enabled }: { method: string; enabled: boolean }) => {
      const methods = enabled
        ? [...companyPaymentMethods.filter(cm => cm !== method)]
        : [...companyPaymentMethods, method];
      dispatch(
        updateCompanyPaymentMethods({
          companyId,
          data: { methods },
        })
      );
    },
    [companyId, companyPaymentMethods, dispatch]
  );

  const { uploadContentImage } = useCompanyImage({
    companyId,
  });
  const handleUpload = useCallback(
    (file: UploadFile, imageType: 'ver' | 'hor') => {
      uploadContentImage(file as unknown as File, imageType);
    },
    [uploadContentImage]
  );

  useEffect(() => {
    dispatch(fetchCompanyPaymentProviders({ companyId }));
    dispatch(fetchPaymentMethods({}));
  }, [companyId, dispatch]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Настройки</Typography.Title>
      </div>
      <div>
        <Typography.Title level={5}>Логотип</Typography.Title>
        <Space direction={'vertical'} style={{ width: '100%' }}>
          {company.image && <Image src={company.image} width={320} />}
          <Upload
            beforeUpload={() => false}
            accept={'image/*'}
            showUploadList={false}
            onChange={e => handleUpload(e.file, 'hor')}
          >
            <Button type={'primary'}>{company.image ? 'Обновить лого' : 'Загрузить лого'}</Button>
          </Upload>
        </Space>
      </div>
      <div>
        <Typography.Title level={5}>Платежные провайдеры</Typography.Title>
        <div>
          {companyPaymentProviders && (
            <div className={tileStyles.listTile}>
              <Row justify='space-between' style={{ width: '100%' }}>
                <Space>
                  <ApiTwoTone />
                  {companyPaymentProviders.provider}
                </Space>
                <Button
                  danger
                  onClick={() =>
                    dispatch(
                      deleteCompanyPaymentProvider({
                        companyId,
                        providerId: companyPaymentProviders.id,
                      })
                    )
                  }
                >
                  Удалить
                </Button>
              </Row>
            </div>
          )}
        </div>
        {!companyPaymentProviders && (
          <Space direction='vertical' size='large'>
            <Typography.Text>Нет подключенных провайдеров</Typography.Text>
            <Select<string>
              placeholder='Выберите провайдера'
              onSelect={e => setNewPaymentProvider(pp => ({ ...pp, provider: e }))}
            >
              {PAYMENT_PROVIDERS.map(pp => (
                <Select.Option key={pp.provider} value={pp.provider}>
                  {pp.provider}
                </Select.Option>
              ))}
            </Select>
            {newPaymentProvider.provider === 'alfa' && (
              <>
                <Input
                  type='text'
                  placeholder='Введите token'
                  onChange={e =>
                    setNewPaymentProvider(pp => ({
                      ...pp,
                      credentials: { ...pp.credentials, Token: e.target.value },
                    }))
                  }
                />
              </>
            )}
            {newPaymentProvider.provider === 'yoomoney' && (
              <>
                <Input
                  type='text'
                  placeholder='Введите AccountId'
                  onChange={e =>
                    setNewPaymentProvider(pp => ({
                      ...pp,
                      credentials: { ...pp.credentials, AccountId: e.target.value },
                    }))
                  }
                />
                <Input
                  type='text'
                  placeholder='Введите AccountSecretKey'
                  onChange={e =>
                    setNewPaymentProvider(pp => ({
                      ...pp,
                      credentials: {
                        ...pp.credentials,
                        AccountSecretKey: e.target.value,
                      },
                    }))
                  }
                />
              </>
            )}
            {newPaymentProvider.provider === 'stripe' && (
              <>
                <Input
                  type='text'
                  placeholder='Введите AccountSecretKey'
                  onChange={e =>
                    setNewPaymentProvider(pp => ({
                      ...pp,
                      credentials: {
                        ...pp.credentials,
                        AccountSecretKey: e.target.value,
                      },
                    }))
                  }
                />
              </>
            )}
            {/*<Input*/}
            {/*  type='text'*/}
            {/*  placeholder='Введите key'*/}
            {/*  onChange={e =>*/}
            {/*    setNewPaymentProvider(pp => ({*/}
            {/*      ...pp,*/}
            {/*      credentials: { ...pp.credentials, key: e.target.value },*/}
            {/*    }))*/}
            {/*  }*/}
            {/*/>*/}
            <Button
              onClick={() =>
                dispatch(
                  createCompanyPaymentProvider({
                    companyId,
                    data: newPaymentProvider,
                  })
                )
              }
            >
              Create
            </Button>
          </Space>
        )}
        <Divider />
        <Typography.Title level={5}>Платежные методы</Typography.Title>
        <div>
          {mergedPaymentMethods.map(m => (
            <div key={m.method} className={tileStyles.listTile}>
              <Row justify='space-between' style={{ width: '100%' }}>
                <Space>
                  <DollarTwoTone />
                  {m.method}
                </Space>
                <Button danger={m.enabled} type='dashed' onClick={() => handleUpdateCompanyPaymentMethods(m)}>
                  {m.enabled ? 'Удалить' : 'Добавить'}
                </Button>
              </Row>
            </div>
          ))}
        </div>
        <Divider />
        <Typography.Title level={5}>Настройки компании</Typography.Title>
        <CompanySettingsSection />
      </div>
    </div>
  );
};

export default CompanySettingsTab;
